import React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled, { useTheme } from "styled-components";

export interface BarLineChartPoint {
  label: string;
  barValue: number;
  lineValue: number;
}

export interface BarLineChartProps {
  data: BarLineChartPoint[];
  barLabel: string;
  lineLabel: string;
  formatValue: (value: number) => string;
  ariaLabel?: string;
  barMaxSize?: number;
}

const Surface = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;

  .recharts-surface {
    overflow: visible;
  }
`;

const BarLineChart: React.FC<BarLineChartProps> = ({
  data,
  barLabel,
  lineLabel,
  formatValue,
  ariaLabel = "Комбинированный график",
  barMaxSize = 24,
}) => {
  const theme = useTheme();
  const xInterval: number | "preserveStartEnd" = data.length > 12
    ? Math.max(0, Math.floor(data.length / 10) - 1)
    : "preserveStartEnd";

  return (
    <Surface role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 18, right: 8, bottom: 4, left: 4 }}>
          <CartesianGrid vertical={false} stroke={theme.borders.divider} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            interval={xInterval}
            minTickGap={12}
            tick={{ fill: theme.colors.inkMuted, fontSize: 12 }}
          />
          <YAxis
            yAxisId="joined"
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={52}
            tick={{ fill: theme.colors.inkMuted, fontSize: 12 }}
          />
          <YAxis
            yAxisId="total"
            orientation="right"
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={52}
            tick={{ fill: theme.colors.inkMuted, fontSize: 12 }}
          />
          <Tooltip
            formatter={(value, name) => [
              formatValue(Number(value)),
              name === "barValue" ? barLabel : lineLabel,
            ]}
            contentStyle={{
              border: `1px solid ${theme.borders.outline}`,
              borderRadius: 10,
              background: theme.colors.white,
              color: theme.colors.ink,
              boxShadow: "0 4px 16px rgba(31, 10, 53, 0.10)",
              fontSize: 13,
              padding: "9px 12px",
            }}
            labelStyle={{ color: theme.colors.inkSoft, marginBottom: 4, fontWeight: 500 }}
            cursor={{ fill: theme.colors.brandSubtle }}
          />
          <Bar
            yAxisId="joined"
            dataKey="barValue"
            fill={theme.colors.brandGlow}
            maxBarSize={barMaxSize}
            radius={[4, 4, 0, 0]}
            animationDuration={320}
          />
          <Line
            yAxisId="total"
            type="linear"
            dataKey="lineValue"
            stroke={theme.colors.brand}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: theme.colors.brand }}
            animationDuration={320}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Surface>
  );
};

export default BarLineChart;
